import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiUrl } from 'Services/Services';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ConfirmModal from 'components/Modal/ReuseableModal';
import { toast } from 'react-toastify';

function ViewFood() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [food, setFood] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    const fetchFood = async () => {
      try {
        const response = await axios.get(`${ApiUrl}/food/${id}`);
        setFood(response.data.food);
      } catch (error) {
        console.log(error.message);
      }
    };
    fetchFood();
  }, [id]);

  const confirmDelete = (id) => {
    setPendingDeleteId(id);
    setShowConfirm(true);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await axios.delete(`${ApiUrl}/food/${pendingDeleteId}`);
      toast.success('Food item deleted');
      navigate('/admin/food');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete');
    } finally {
      setShowConfirm(false);
      setPendingDeleteId(null);
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/editfood/${id}`);
  };

  if (!food) return <div>Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto mt-10 bg-white shadow-lg rounded-xl p-6 space-y-6">
      {/* Actions */}
      <div className="flex justify-end">
        <button
          onClick={() => handleEdit(food.id)}
          className="text-blue-500 hover:text-blue-700 ml-3"
        >
          <FaEdit />
        </button>
        <button
          onClick={() => confirmDelete(food.id)}
          className="text-red-500 hover:text-red-700 ml-3"
        >
          <FaTrash />
        </button>
      </div>

      {/* Image */}
      <div className="w-full flex justify-center">
        <img
          src={food.image}
          alt={food.name}
          className="w-72 h-72 object-cover rounded-lg shadow border"
        />
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-gray-600 font-medium">Name</label>
          <p className="text-gray-800 mt-1">{food.name}</p>
        </div>
        <div>
          <label className="block text-gray-600 font-medium">Price</label>
          <p className="text-gray-800 mt-1">Rs. {food.price}</p>
        </div>
        <div>
          <label className="block text-gray-600 font-medium">Category</label>
          <p className="text-gray-800 mt-1">{food.category?.title}</p>
        </div>
      </div>

      {/* Description */}
      <div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Description</h3>
        <div
          className="prose prose-sm max-w-none text-gray-800"
          dangerouslySetInnerHTML={{ __html: food.description }}
        ></div>
      </div>

      {/* Variations */}
      {food.variations?.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Variations</h3>
          <div className="space-y-2">
            {food.variations.map((v) => (
              <div
                key={v.id}
                className="flex justify-between bg-gray-50 px-4 py-2 rounded border"
              >
                <span>{v.name}</span>
                <span className="text-blue-600 font-medium">Rs. {v.price}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ingredients */}
      {food.ingredients?.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Ingredients</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200 rounded">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Available Qty</th>
                  <th className="px-4 py-2 text-left">Unit</th>
                  <th className="px-4 py-2 text-left">Used in Food</th>
                </tr>
              </thead>
              <tbody>
                {food.ingredients.map((ing) => (
                  <tr key={ing.id} className="border-t">
                    <td className="px-4 py-2">{ing.name}</td>
                    <td className="px-4 py-2">{ing.quantity}</td>
                    <td className="px-4 py-2">{ing.unit}</td>
                    <td className="px-4 py-2">
                      {ing.FoodIngredient?.quantity || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirm}
        message="Are you sure you want to delete this food item?"
        type="danger"
        onCancel={() => {
          setShowConfirm(false);
          setPendingDeleteId(null);
        }}
        onConfirm={handleDeleteConfirmed}
      />
    </div>
  );
}

export default ViewFood;
